import { useState, useEffect } from 'react';

/**
 * Returns a dynamic bottom padding in pixels that increases on smaller screens
 * and flattens on larger screens using a responsive curve.
 *
 * @param {number} minPaddingPx - Minimum padding in px (large screens)
 * @param {number} maxPaddingPx - Maximum padding in px (small screens)
 * @param {number} referenceHeightPx - The window height in px considered "normal"
 * @param {number} exponent - Controls curve steepness (default 1.5)
 */
const useDynamicPaddingBottom = (
  minPaddingPx = 50, // default min 50px
  maxPaddingPx = 160, // default max 160px
  referenceHeightPx = 900, // "normal" screen height
  exponent = 1.5
) => {
  const [paddingBottom, setPaddingBottom] = useState(`${minPaddingPx}px`);

  useEffect(() => {
    const updatePadding = () => {
      const height = window.innerHeight;

      // Smaller height → bigger padding
      let ratio = referenceHeightPx / height;
      ratio = Math.pow(ratio, exponent);

      let padding = minPaddingPx * ratio;

      // Clamp between min/max
      padding = Math.min(Math.max(padding, minPaddingPx), maxPaddingPx);

      setPaddingBottom(`${padding}px`);
    };

    updatePadding(); // initial calculation
    window.addEventListener('resize', updatePadding);

    return () => window.removeEventListener('resize', updatePadding);
  }, [minPaddingPx, maxPaddingPx, referenceHeightPx, exponent]);

  return paddingBottom;
};

export default useDynamicPaddingBottom;
